import React, { useEffect, useState } from 'react';
import { ArrowRight, Star } from 'lucide-react';
import { GOOGLE_REVIEWS_URL, GOOGLE_REVIEWS_WIDGET_ID } from '../../../data/homeContent';
import { SectionHeading } from './CoreSections';

const API = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ n }) => (
  <span className="flex gap-0.5 text-[#B89B5E]" aria-label={`${n} von 5 Sternen`}>
    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4" fill={i < n ? 'currentColor' : 'none'} aria-hidden="true" />)}
  </span>
);

export const ReviewsSection = () => {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/reviews?limit=6`).then((r) => (r.ok ? r.json() : { reviews: [] })).then((d) => setReviews(d.reviews || [])).catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    if (!GOOGLE_REVIEWS_WIDGET_ID || reviews === null || reviews.length > 0 || document.querySelector('script[src*="elfsight"]')) return;
    const s = document.createElement('script'); s.src = 'https://static.elfsight.com/platform/platform.js'; s.defer = true; s.setAttribute('data-use-service-core', ''); document.body.appendChild(s);
  }, [reviews]);

  const [lead, ...rest] = reviews || [];

  return (
    <section className="hv-reviews bg-[#F8F6F0] py-24 lg:py-32" data-testid="homepage-reviews">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeading kicker="Google-Bewertungen" title="Was Eigentümer nach dem Verkauf sagen." text="Ausgewählte, echte Bewertungen von Heinrichs Immobilien auf Google – im Original übernommen." />
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link" className="hv-link inline-flex items-center gap-2 mb-14 text-[#19392B] font-medium underline underline-offset-8 decoration-[#DFC590]">
            Alle Bewertungen auf Google <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>

        {reviews && reviews.length > 0 && (
          <div className="grid lg:grid-cols-12 gap-5" data-testid="reviews-grid">
            <figure className="hv-review hv-review-lead lg:col-span-5 bg-[#153B2B] text-[#F8F6F0] rounded-[4px] p-8 lg:p-10 flex flex-col" data-testid="review-card-lead">
              <Stars n={lead.rating} />
              <blockquote className="hv-serif text-[26px] lg:text-[30px] leading-[1.3] mt-6 mb-8 flex-1">„{lead.highlight || lead.text}“</blockquote>
              {lead.highlight && <p className="text-[#C9D3C8] leading-relaxed mb-6">{lead.text}</p>}
              <figcaption className="flex items-center justify-between gap-4 text-sm">
                <span><span className="font-medium text-[#F8F6F0]">{lead.author}</span>{lead.date_label && <span className="text-[#A9B8A9]"> · {lead.date_label}</span>}</span>
                <span className="text-[#DFC590]">Google</span>
              </figcaption>
            </figure>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {rest.slice(0, 4).map((rv) => (
                <figure key={rv.id} className="hv-review bg-[#FFFEFA] border border-[#DEDFD3] rounded-[4px] p-6 lg:p-7 flex flex-col" data-testid={`review-card-${rv.id}`}>
                  <Stars n={rv.rating} />
                  <blockquote className="text-[#19392B] leading-relaxed mt-4 mb-5 flex-1">„{rv.text}“</blockquote>
                  <figcaption className="flex items-center justify-between gap-4 text-sm">
                    <span><span className="font-medium text-[#19392B]">{rv.author}</span>{rv.date_label && <span className="text-[#647064]"> · {rv.date_label}</span>}</span>
                    <span className="text-[#647064]">Google</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}

        {reviews && reviews.length === 0 && GOOGLE_REVIEWS_WIDGET_ID && (
          <div className={`elfsight-app-${GOOGLE_REVIEWS_WIDGET_ID}`} data-elfsight-app-lazy data-testid="google-reviews-widget" />
        )}

        {reviews && reviews.length === 0 && !GOOGLE_REVIEWS_WIDGET_ID && (
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="reviews-placeholder" className="hv-card block bg-[#FFFEFA] border border-[#DEDFD3] rounded-[4px] p-8 lg:p-10 max-w-[720px]">
            <Stars n={5} />
            <p className="hv-serif text-2xl text-[#19392B] mt-5 mb-3">Unsere Bewertungen lesen Sie direkt auf Google.</p>
            <p className="text-[#647064]">Heinrichs Immobilien, Rindermarkt 8, Freising – ungefiltert und nachprüfbar.</p>
          </a>
        )}
      </div>
    </section>
  );
};
